import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../../constants/layout/store/filterSlice";
import Select from "react-select";
import { API_URL } from "../../../../../constants";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import {
  GetServiceCaller,
  PostServiceCaller,
} from "../../../../../services/serviceCaller";
import BulkUploadOtherDeductionModal from "./BulkUploadOtherDeductionModal";

const IncentiveCCA = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updateNavbarHeading("Incentives/CCA"));
    fetchIncentiveCCA();
    fetchPayrollMonth();
  }, []);

  const initialFormData = {
    PayrollID: "",
    EmployeeID: "",
    ProjectID: "",
    CityID: "",
    Amount: "",
    PayrollMonthID: "",
    TXNID: 0,
    ExchangeRate: 1,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [typeList, setTypeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empList, setEmpList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [payrollList, setPayrollList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => {
    if (formData.EmployeeID) {
      fetchProjectDetails(formData.EmployeeID);
      setProjectList([]);
    }
  }, [formData.EmployeeID]);
  const fetchProjectDetails = async (id) => {
    try {
      const queryParams = new URLSearchParams({
        EmployeeID: id,
      });
      const response = await GetServiceCaller(
        `${API_URL}payroll/incentive&CCA/get-employee-project-list?${queryParams.toString()}`
      );
      if (response.status === 200) {
        const fetchData = response?.data?.data;
        const brandlist = fetchData?.map((ele, index) => ({
          value: ele?.value,
          label: ele?.text,
        }));
        setProjectList(brandlist);
      } else {
        console.error(
          "Failed to fetch model masters. Server responded with status:",
          response.status
        );
      }
    } catch (error) {
      console.error("An error occurred while fetching brand details:", error);
    }
  };

  const fetchIncentiveCCA = async () => {
    try {
      const response = await GetServiceCaller(
        `${API_URL}payroll/incentive&CCA/incentive-cca`
      );

      // Type Options
      const typeList = response?.data?.data?.type;
      const typeOpt = typeList.map((item) => ({
        value: item.value,
        label: item.text,
      }));
      setTypeList(typeOpt);

      // Employee List
      const empList = response?.data?.data?.employeeList;
      const empOpt = empList.map((item) => ({
        value: item.value,
        label: item.text,
      }));
      setEmpList(empOpt);

      // City List
      const cityList = response?.data?.data?.cities;
      const cityOpt = cityList.map((item) => ({
        value: item.value,
        label: item.text,
      }));
      setCitiesList(cityOpt);
      setPageLoading(false);
    } catch (error) {
      console.error("Error fetching TnE:", error);
    }
  };

  const fetchPayrollMonth = async () => {
    try {
      const response = await GetServiceCaller(
        `${API_URL}payroll/incentive&CCA/get-employee-payroll-months`
      );

      // Type Options
      const payroll = response?.data?.data;
      const payrollOpt = payroll.map((item) => ({
        value: item.value,
        label: item.text,
      }));
      setPayrollList(payrollOpt);
    } catch (error) {
      console.error("Error fetching TnE:", error);
    }
  };


  const getValidationSchema = (formData) => {
    let dynamicSchema = Yup.object().shape({
      PayrollID: Yup.number()
        .typeError("Type is required")
        .required("Type is required")
        .nullable(),
      EmployeeID: Yup.number()
        .typeError("Employee is required")
        .required("Employee is required")
        .nullable(),
      PayrollMonthID: Yup.number()
        .typeError("Payroll Month is required")
        .required("Payroll Month is required")
        .nullable(),
    });

    // Add CityID validation only if PayrollID is 5
    if (formData?.PayrollID === 5) {
      dynamicSchema = dynamicSchema.shape({
        CityID: Yup.number()
          .typeError("City is required")
          .required("City is required")
          .nullable(),
      });
    }

    return dynamicSchema;
  };

  const validateField = async (fieldName, updatedData) => {
    try {
      const schema = getValidationSchema(updatedData); // Get the schema dynamically
      await schema.validateAt(fieldName, updatedData);

      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    } catch (err) {
      if (err.path === fieldName) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: err.message,
        }));
      } else {
        console.error("Validation error structure is not as expected:", err);
      }
    }
  };

  // Input field change handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update form data for all fields
    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      return updatedData;
    });
  };

  const handleSelect = async (selectedOption, name) => {
    // Update formData with the selected value for the SkillID
    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: selectedOption.value };
      // Trigger validation for the specific field after updating
      validateField(name, updatedData);
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const updatedFormData = {
      ...formData,
      Amount: formData.Amount.trim() === "" ? 0 : formData.Amount,
    };

    try {
      const schema = getValidationSchema(updatedFormData);
      await schema.validate(updatedFormData, { abortEarly: false });
      setErrors({}); // Clear previous errors

      // Call the API
      const response = await PostServiceCaller(
        `${API_URL}payroll/incentive&CCA/incentive-cca`,
        updatedFormData,
        "post"
      );

      if (response.status === 200) {
        toast.success("Data submitted successfully");
        setFormData(initialFormData);
        window.location.reload();
      } else {
        console.error(
          "Failed to submit data. Server responded with status:",
          response.status
        );
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        console.error("Validation Error:", error);
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        console.error("Unexpected Error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <>
        <p class="position-absolute top-50 start-50 translate-middle text-center">
          <FaSpinner class="spinner" />
        </p>
      </>
    );
  } else {
    return (
      <>
        <div className="container-fluid" id="skill_section">
          <div className="container-fluid" id="skill-table-area">
            <div className="row p-2">
            {/* <div className="text-end mb-2">
              <button
                type="button"
                className="custom-btn btn-sm"
                onClick={() => setShowBulkUpload(true)}
              >
                Bulk Upload Other Deduction
              </button>
            </div> */}
            <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-2">
                    <div class="mb-3">
                      <label for="Code" class="form-label">
                        Type<span className="required">*</span>
                      </label>
                      <Select
                        value={typeList?.find(
                          (o) => o.value === formData?.PayrollID
                        )}
                        className={` ${errors.PayrollID ? "is-invalid" : ""}`}
                        options={[
                          { value: null, label: "Select" },
                          ...typeList,
                        ]}
                        name="PayrollID"
                        onChange={(selectedOption, action) =>
                          handleSelect(selectedOption, action.name)
                        }
                      />
                      {errors.PayrollID && (
                        <div className="invalid-feedback">
                          {errors.PayrollID}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div class="mb-3">
                      <label for="Code" class="form-label">
                        Employee<span className="required">*</span>
                      </label>
                      <Select
                        value={empList?.find(
                          (o) => o.value === formData?.EmployeeID
                        )}
                        name="EmployeeID"
                        className={` ${errors.EmployeeID ? "is-invalid" : ""}`}
                        onChange={(selectedOption, action) =>
                          handleSelect(selectedOption, action.name)
                        }
                        options={[{ value: null, label: "Select" }, ...empList]}
                      />
                      {errors.EmployeeID && (
                        <div className="invalid-feedback">
                          {errors.EmployeeID}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div class="mb-3">
                      <label for="Code" class="form-label">
                        Project
                      </label>
                      <Select
                        value={projectList?.find(
                          (o) => o.value === formData?.ProjectID
                        )}
                        options={[
                          { value: null, label: "Select" },
                          ...projectList,
                        ]}
                        name="ProjectID"
                        onChange={(selectedOption, action) =>
                          handleSelect(selectedOption, action.name)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div class="mb-3">
                      <label for="Code" class="form-label">
                        Amount
                      </label>
                      <input
                        type="number"
                        className="form-control text-end"
                        placeholder="0.00"
                        name="Amount"
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div class="mb-3">
                      <label for="Code" class="form-label">
                        Payroll Month<span className="required">*</span>
                      </label>
                      <Select
                        value={payrollList?.find(
                          (o) => o.value === formData?.PayrollMonthID
                        )}
                        options={[
                          { value: null, label: "Select" },
                          ...payrollList,
                        ]}
                        className={` ${errors.PayrollMonthID ? "is-invalid" : ""
                          }`}
                        name="PayrollMonthID"
                        onChange={(selectedOption, action) =>
                          handleSelect(selectedOption, action.name)
                        }
                      />
                      {errors.PayrollMonthID && (
                        <div className="invalid-feedback">
                          {errors.PayrollMonthID}
                        </div>
                      )}
                    </div>
                  </div>
                  {formData?.PayrollID === 5 && (
                    <div className="col-md-2">
                      <div class="mb-3">
                        <label for="Code" class="form-label">
                          City<span className="required">*</span>
                        </label>
                        <Select
                          value={citiesList?.find(
                            (o) => o.value === formData?.CityID
                          )}
                          className={` ${errors.CityID ? "is-invalid" : ""}`}
                          options={[
                            { value: null, label: "Select" },
                            ...citiesList,
                          ]}
                          name="CityID"
                          onChange={(selectedOption, action) =>
                            handleSelect(selectedOption, action.name)
                          }
                        />
                        {errors.CityID && (
                          <div className="invalid-feedback">
                            {errors.CityID}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

              <div className="text-end">
                <button type="button" className="cancel-btn me-2">
                  Cancel
                </button>
                <button type="submit" className="custom-btn">
                  {loading ? <FaSpinner class="sm-spinner" /> : "Submit"}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
        <BulkUploadOtherDeductionModal
          show={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onImported={() => window.location.reload()}
          payrollList={payrollList}
        />
      </>
    );
  }
};

export default IncentiveCCA;
